import { useContext, useCallback, useMemo } from 'react';

import feedType from '../../CoachTools/context/FeedContext/config/feedTypes';
import PodContext from './PodContext';
import { ALL_CLIENTS } from './constants';

/**
 * Checks if a document matches the filter criteria based on the provided key.
 *
 * @param {Object} doc - The document to check
 * @param {string} key - The key to check in the document
 * @param {Set<string>} set - The set of user IDs to check against
 * @returns {boolean} - True if the document matches the filter criteria, false otherwise
 */
function documentMatchesFilter(doc, key, set) {
  // Early return for special cases
  if (!doc || !key || set.size === 0) return false;

  // Handle special feed type case
  if (doc.data?.type === feedType.SPECIAL && doc.data?.coach === doc.data?.user) {
    return true;
  }

  const value = doc[key];
  if (!value) return false;

  // Handle array values
  if (Array.isArray(value)) {
    return value.some((item) => set.has(item));
  }

  // Handle single value
  return set.has(value);
}

const usePodFilter = () => {
  const { users: podUsers, selectedPodId, isReady } = useContext(PodContext);

  const shouldFilter = useMemo(
    () => isReady && !!selectedPodId && selectedPodId !== ALL_CLIENTS,
    [isReady, selectedPodId],
  );

  const podUsersSet = useMemo(
    () => new Set(Array.isArray(podUsers) ? podUsers : []),
    [podUsers],
  );

  const getFilteredDocs = useCallback((docs, key) => {
    // Early returns for common cases
    if (!shouldFilter || !Array.isArray(docs) || !key) return docs;

    return docs.filter((doc) => documentMatchesFilter(doc, key, podUsersSet));
  }, [podUsersSet, shouldFilter]);

  return {
    getFilteredDocs,
    shouldFilter,
    podUsersCount: podUsersSet.size,
  };
};

export { usePodFilter };
